import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "./api";
import { getIdentity, setIdentity, clearIdentity } from "./learnerIdentity";
import { clearAccessCache } from "./useModuleAccess";

const AuthCtx = createContext({ user: null, loading: true, login: () => {}, logout: () => {}, register: () => {} });

const TOKEN_KEY = "maldek_token";

export function getToken() {
  return localStorage.getItem(TOKEN_KEY) || null;
}

export function setToken(t) {
  if (t) localStorage.setItem(TOKEN_KEY, t);
  else localStorage.removeItem(TOKEN_KEY);
}

// axios interceptor to attach Bearer
api.interceptors.request.use((cfg) => {
  const t = getToken();
  if (t) cfg.headers = { ...cfg.headers, Authorization: `Bearer ${t}` };
  // Only enable credentials on the Emergent google-exchange endpoint
  // (it must set the session_token cookie). Wildcard CORS origin doesn't
  // allow credentials globally.
  if (cfg.url === "/auth/google/exchange") cfg.withCredentials = true;
  return cfg;
});

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    // CRITICAL: skip /auth/me if we are landing from Emergent OAuth fragment.
    // AuthCallback page exchanges session_id first.
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    // Skip /auth/me entirely if no token is stored — public pages don't need
    // to ping the backend (and the resulting 401 was polluting DevTools and
    // making it look like pages were "not loading").
    if (!getToken()) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const r = await api.get("/auth/me");
      setUser(r.data.user);
      // Phase 4 bridge: mirror the JWT user into the legacy "learner identity"
      // store so the walled-garden `RequireIdentity` gate lifts for them.
      // Best-effort and silent — never block on this.
      try {
        const u = r.data.user || {};
        if (u.email && !getIdentity()) {
          await setIdentity({
            email:          u.email,
            display_name:   u.name || u.email.split("@")[0],
            cohort_id:      u.cohort_id || null,
            institution_id: u.org_id || null,
          });
        }
      } catch { /* non-fatal */ }
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  async function login(email, password) {
    const r = await api.post("/auth/login", { email, password });
    setToken(r.data.token);
    setUser(r.data.user);
    // Mirror into legacy identity so the walled-garden lifts immediately.
    try {
      const u = r.data.user || {};
      await setIdentity({
        email:          u.email,
        display_name:   u.name || (u.email || "").split("@")[0],
        cohort_id:      u.cohort_id || null,
        institution_id: u.org_id || null,
      });
    } catch { /* non-fatal */ }
    return r.data.user;
  }

  async function register(email, password, name) {
    const r = await api.post("/auth/register", { email, password, name });
    // Phase-1: register no longer auto-logs in. Backend returns
    // { user, verification_required: true } — caller shows a "check
    // your email" screen and waits for /verify-email to issue a token.
    if (r.data?.token) {
      setToken(r.data.token);
      setUser(r.data.user);
    }
    return r.data;
  }

  async function logout() {
    try {
      await api.post("/auth/logout");
    } catch { /* logout best-effort */ }
    setToken(null);
    setUser(null);
    // Drop the legacy identity too so the walled-garden re-engages.
    try { clearIdentity(); } catch { /* noop */ }
    // Phase 5 — drop the cached access map so the next login starts fresh.
    try { clearAccessCache(); } catch { /* noop */ }
  }

  async function exchangeEmergent(session_id) {
    const r = await api.post("/auth/google/exchange", { session_id });
    setToken(r.data.token);
    setUser(r.data.user);
    return r.data.user;
  }

  return (
    <AuthCtx.Provider value={{ user, loading, login, logout, register, exchangeEmergent, refresh }}>
      {children}
    </AuthCtx.Provider>
  );
}

export function useAuth() {
  return useContext(AuthCtx);
}
